import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { Plus, Truck as TruckIcon, MagnifyingGlass, SquaresFour, ListBullets } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { TRUCK_TYPES, getTruckConfig, normalizeTruckType } from '../lib/truckConfig';

const sortByNumber = (a, b) => {
  const na = parseInt(a.truck_number, 10);
  const nb = parseInt(b.truck_number, 10);
  if (isNaN(na) && isNaN(nb)) return 0;
  if (isNaN(na)) return 1;
  if (isNaN(nb)) return -1;
  return na - nb;
};

export default function Trucks() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [trucks, setTrucks] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({ truck_number: '', type: TRUCK_TYPES[0], custom_id: '' });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('card');

  useEffect(() => { if (profile) fetchTrucks(); }, [profile]);

  const fetchTrucks = async () => {
    try {
      let result = await supabase
        .from('trucks')
        .select('*, tire_positions(id, tire_id, dismounted_date)')
        .eq('company_id', profile.company_id)
        .is('deleted_at', null);
      // Fallback if migration not yet applied (column missing => 400)
      if (result.error) {
        result = await supabase
          .from('trucks')
          .select('*, tire_positions(id, tire_id, dismounted_date)')
          .eq('company_id', profile.company_id);
      }
      if (result.error) throw result.error;
      const data = result.data;
      const trucksWithStats = (data || []).map(truck => {
        const mountedCount = truck.tire_positions?.filter(pos => pos.tire_id !== null && !pos.dismounted_date).length || 0;
        return { ...truck, mountedCount, normalizedType: normalizeTruckType(truck.type) };
      });
      trucksWithStats.sort(sortByNumber);
      setTrucks(trucksWithStats);
    } catch (error) {
      console.error('Error fetching trucks:', error);
      toast.error('Gagal memuat armada');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const truckNum = formData.truck_number.trim();
    const customId = formData.custom_id.trim();
    if (!truckNum) {
      toast.error('Nomor Lambung wajib diisi');
      return;
    }
    setLoading(true);
    try {
      // Pre-check duplicates within active (non-deleted) trucks of same company
      const dupTruckNum = trucks.find(
        (t) => t.truck_number?.toLowerCase() === truckNum.toLowerCase(),
      );
      if (dupTruckNum) {
        toast.error(`Nomor Lambung "${truckNum}" sudah dipakai oleh armada lain di perusahaan Anda`);
        setLoading(false);
        return;
      }
      if (customId) {
        const dupCustomId = trucks.find(
          (t) => t.custom_id && t.custom_id.toLowerCase() === customId.toLowerCase(),
        );
        if (dupCustomId) {
          toast.error(`ID Armada "${customId}" sudah dipakai (Nomor Lambung: ${dupCustomId.truck_number})`);
          setLoading(false);
          return;
        }
      }

      const config = getTruckConfig(formData.type);
      const { data: truck, error: truckError } = await supabase
        .from('trucks').insert([{
          company_id: profile.company_id,
          truck_number: truckNum,
          type: formData.type,
          custom_id: customId || null,
          num_wheels: config.wheels,
        }]).select().single();
      if (truckError) {
        // PostgreSQL unique violation = 23505
        if (truckError.code === '23505' || /duplicate key/i.test(truckError.message || '')) {
          if (/truck_number/i.test(truckError.message || '')) {
            toast.error(`Nomor Lambung "${truckNum}" sudah dipakai`);
          } else if (/custom_id/i.test(truckError.message || '')) {
            toast.error(`ID Armada "${customId}" sudah dipakai`);
          } else {
            toast.error('Data duplikat: cek Nomor Lambung & ID Armada');
          }
          setLoading(false);
          return;
        }
        throw truckError;
      }

      const positions = Array.from({ length: config.wheels }, (_, i) => ({
        truck_id: truck.id, position_number: i + 1, tire_id: null,
      }));
      const { error: posError } = await supabase.from('tire_positions').insert(positions);
      if (posError) throw posError;

      toast.success('Armada berhasil ditambahkan');
      setShowAddForm(false);
      setFormData({ truck_number: '', type: TRUCK_TYPES[0], custom_id: '' });
      fetchTrucks();
    } catch (error) {
      console.error('Error adding truck:', error);
      toast.error(error.message || 'Gagal menambahkan armada');
    } finally { setLoading(false); }
  };

  const filtered = trucks.filter(t => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return t.truck_number?.toLowerCase().includes(q) || t.custom_id?.toLowerCase().includes(q);
  });

  // Group by normalized type
  const grouped = filtered.reduce((acc, t) => {
    const key = t.normalizedType;
    if (!acc[key]) acc[key] = [];
    acc[key].push(t);
    return acc;
  }, {});
  const groupOrder = [...TRUCK_TYPES, ...Object.keys(grouped).filter(k => !TRUCK_TYPES.includes(k))];
  const visibleGroups = groupOrder.filter(k => grouped[k]?.length > 0);

  const renderCard = (truck) => (
    <div
      key={truck.id}
      onClick={() => navigate(`/trucks/${truck.id}`)}
      className="bg-white border border-zinc-200 rounded-xl p-6 cursor-pointer hover:bg-zinc-50 hover:border-red-300 transition-colors shadow-sm"
      data-testid={`truck-card-${truck.truck_number}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-1">Nomor Lambung</p>
          <p className="text-3xl font-mono text-zinc-900 tracking-tight font-bold">{truck.truck_number}</p>
        </div>
        <TruckIcon size={32} weight="bold" className="text-zinc-400" />
      </div>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Tipe</p>
          <p className="text-sm text-zinc-900 font-mono">{truck.type}</p>
        </div>
        {truck.custom_id && (
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">ID</p>
            <p className="text-sm text-zinc-900 font-mono">{truck.custom_id}</p>
          </div>
        )}
      </div>
      <div className="border-t border-zinc-200 pt-4">
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-1">Ban Terdata</p>
        <p className="text-lg font-mono text-zinc-900">{truck.mountedCount} / {truck.num_wheels || 12}</p>
      </div>
    </div>
  );

  return (
    <div className="p-4 sm:p-8" data-testid="trucks-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-widest text-zinc-900" style={{ fontFamily: 'Chivo' }}>
          ARMADA
        </h1>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-red-600 hover:bg-red-700 text-white font-mono uppercase text-xs tracking-wider py-3 px-6 flex items-center gap-2 transition-colors rounded-lg shadow-sm"
          data-testid="add-truck-button"
        >
          <Plus size={16} weight="bold" />
          TAMBAH ARMADA
        </button>
      </div>

      {showAddForm && (
        <div className="bg-white border border-zinc-200 rounded-xl p-6 mb-8 shadow-sm" data-testid="add-truck-form">
          <h2 className="text-xl sm:text-2xl font-black uppercase tracking-wider text-zinc-900 border-b border-zinc-200 pb-2 mb-6" style={{ fontFamily: 'Chivo' }}>
            ARMADA BARU
          </h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 block mb-2">Nomor Lambung *</label>
              <input
                type="number" value={formData.truck_number}
                onChange={(e) => setFormData({ ...formData, truck_number: e.target.value })}
                placeholder="contoh: 101"
                className="w-full bg-white border border-zinc-300 rounded-lg p-3 text-zinc-900 font-mono focus:border-red-500 focus:ring-1 focus:ring-red-500 focus:outline-none"
                required data-testid="truck-number-input"
              />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 block mb-2">Tipe *</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                className="w-full bg-white border border-zinc-300 rounded-lg p-3 text-zinc-900 font-mono focus:border-red-500 focus:ring-1 focus:ring-red-500 focus:outline-none"
                data-testid="truck-type-select"
              >
                {TRUCK_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 block mb-2">ID Armada (opsional)</label>
              <input
                type="text" value={formData.custom_id}
                onChange={(e) => setFormData({ ...formData, custom_id: e.target.value })}
                className="w-full bg-white border border-zinc-300 rounded-lg p-3 text-zinc-900 font-mono focus:border-red-500 focus:ring-1 focus:ring-red-500 focus:outline-none"
                data-testid="truck-id-input"
              />
            </div>
            <div className="md:col-span-3 flex gap-3">
              <button
                type="submit" disabled={loading}
                className="bg-red-600 hover:bg-red-700 text-white font-mono uppercase text-xs tracking-wider py-3 px-6 rounded-lg shadow-sm transition-colors disabled:opacity-50"
                data-testid="submit-truck-button"
              >
                {loading ? 'MENAMBAHKAN...' : 'TAMBAH ARMADA'}
              </button>
              <button
                type="button" onClick={() => setShowAddForm(false)}
                className="border border-zinc-300 hover:bg-zinc-100 text-zinc-900 font-mono uppercase text-xs tracking-wider py-3 px-6 rounded-lg transition-colors"
                data-testid="cancel-truck-button"
              >
                BATAL
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Search + view toggle */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Cari berdasarkan nomor lambung atau ID..."
            className="w-full bg-white border border-zinc-300 rounded-lg p-3 pl-10 text-zinc-900 font-mono focus:border-red-500 focus:ring-1 focus:ring-red-500 focus:outline-none"
            data-testid="truck-search-input"
          />
        </div>
        <div className="flex border border-zinc-300 rounded-lg overflow-hidden">
          <button onClick={() => setViewMode('card')}
            className={`px-4 py-2 flex items-center gap-2 font-mono uppercase text-xs tracking-wider ${viewMode === 'card' ? 'bg-red-600 text-white' : 'bg-white text-zinc-700 hover:bg-zinc-50'}`}
            data-testid="view-card">
            <SquaresFour size={14} weight="bold" />KARTU
          </button>
          <button onClick={() => setViewMode('list')}
            className={`px-4 py-2 flex items-center gap-2 font-mono uppercase text-xs tracking-wider ${viewMode === 'list' ? 'bg-red-600 text-white' : 'bg-white text-zinc-700 hover:bg-zinc-50'}`}
            data-testid="view-list">
            <ListBullets size={14} weight="bold" />LIST
          </button>
        </div>
      </div>

      {/* Grouped content */}
      {viewMode === 'card' ? (
        <div className="space-y-8">
          {visibleGroups.map((typeName) => (
            <section key={typeName} data-testid={`group-${typeName}`}>
              <div className="flex items-center justify-between mb-3 border-b border-zinc-200 pb-2">
                <h2 className="text-lg sm:text-xl font-black uppercase tracking-wider text-zinc-900" style={{ fontFamily: 'Chivo' }}>
                  {typeName}
                </h2>
                <span className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">
                  {grouped[typeName].length} unit
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {grouped[typeName].map(renderCard)}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <div className="space-y-6">
          {visibleGroups.map((typeName) => (
            <section key={typeName} className="bg-white border border-zinc-200 rounded-xl overflow-hidden shadow-sm" data-testid={`group-list-${typeName}`}>
              <div className="flex items-center justify-between p-4 border-b border-zinc-200 bg-zinc-50">
                <h2 className="text-base font-black uppercase tracking-wider text-zinc-900" style={{ fontFamily: 'Chivo' }}>
                  {typeName}
                </h2>
                <span className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">
                  {grouped[typeName].length} unit
                </span>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-200 bg-zinc-50/50">
                    <th className="text-left p-4 text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Nomor Lambung</th>
                    <th className="text-left p-4 text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">ID</th>
                    <th className="text-left p-4 text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Ban Terdata</th>
                  </tr>
                </thead>
                <tbody>
                  {grouped[typeName].map((truck) => (
                    <tr key={truck.id}
                      onClick={() => navigate(`/trucks/${truck.id}`)}
                      className="border-b border-zinc-200 last:border-b-0 hover:bg-zinc-50 cursor-pointer"
                      data-testid={`truck-row-${truck.truck_number}`}>
                      <td className="p-4 text-xl text-zinc-900 font-mono font-bold">{truck.truck_number}</td>
                      <td className="p-4 text-zinc-700 font-mono text-sm">{truck.custom_id || '—'}</td>
                      <td className="p-4 text-zinc-900 font-mono text-sm">{truck.mountedCount} / {truck.num_wheels || 12}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ))}
        </div>
      )}

      {filtered.length === 0 && !showAddForm && (
        <div className="text-center py-20">
          <TruckIcon size={64} weight="bold" className="text-zinc-300 mx-auto mb-4" />
          <p className="text-zinc-500 font-mono text-sm">
            {searchTerm ? 'Tidak ada armada yang cocok' : 'Belum ada armada ditambahkan'}
          </p>
        </div>
      )}
    </div>
  );
}
